<template>
  <article>
    <EventListBanner>
      <EventsSearchForm
        v-model:search-params="searchParams"
        v-model:show-search-results="showSearchResults"
        :search-results="searchResults"
        @search="handleSearch"
      />
    </EventListBanner>
    <!-- Event Cards -->
    <section class="section-events">
      <ul v-if="events.length">
        <EventsListItem
          v-for="event in events"
          :key="event.id"
          :event="event"
        />
      </ul>

      <h2 v-else-if="!loading && !events.length">
        <span>Sorry,</span>
        we couldn't find any events with that name.
      </h2>

      <PhotoGalleryLoader v-if="loading" />

      <AppButton
        v-if="showLoadMoreButton"
        class="btn-solid btn-secondary"
        @click="() => { searchParams.page++; loadEvents() }"
      >
        Load more
      </AppButton>
    </section>

    <EventListGallery />

    <EventListCards />
  </article>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { reactive, ref, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';

import { AppButton } from '@/components/app';

import { EventListBanner } from '@/components/banners';
import { EventListGallery, EventListCards, EventsListItem } from '@/components/events';
import { EventsSearchForm } from '@/components/forms/search';
import { PhotoGalleryLoader } from '@/components/loaders';

import { useHttp } from '@/composables';

const events = ref([]);
const loading = ref(true);

// search results table
const searchResults = ref([]);
const showSearchResults = ref(true);

const searchParams = reactive({
  page: 1,
  search: null,
});

const showLoadMoreButton = ref(false);

const loadEvents = async () => {
  loading.value = true;
  showLoadMoreButton.value = false;

  const response = await useHttp().get('/events', searchParams);

  if (response.statusCode === HttpStatusCode.Ok) {
    const { itemsPerPage, total } = response.data.pagination;

    events.value.push(...response.data.items);
    showLoadMoreButton.value = searchParams.page < (total / itemsPerPage);
  }

  loading.value = false;
};

const handleDebouncedSearch = useDebounceFn(async () => {
  if (!searchParams.search) {
    searchResults.value = [];

    return;
  }

  const response = await useHttp().get('/events', searchParams);

  if (response.statusCode === HttpStatusCode.Ok) {
    searchResults.value = response.data.items.slice(0, 5);
  }

}, 100, { maxWait: 1000 });

const handleSearch = () => {
  searchParams.page = 1;
  events.value = [];
  showSearchResults.value = false;

  loadEvents();
};

loadEvents();

watch(() => searchParams.search, () => {
  handleDebouncedSearch();
});

</script>
